import React, { useState, useEffect } from 'react';
import { LuArrowRight, LuX } from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';

export default function BookingForm({ isOpen, onClose, defaultService }) {
  const [formData, setFormData] = useState({
    nama: '',
    wa: '',
    layanan: 'G-Reg',
    asal: '',
    tujuan: '',
    detail: ''
  });

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Update default service if provided
      if (defaultService) {
        setFormData(prev => ({ ...prev, layanan: defaultService }));
      }
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, defaultService]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = `Halo Garuda Ekspress Logistik, saya ingin melakukan pengiriman paket.%0A%0A*Nama:* ${formData.nama}%0A*No WA:* ${formData.wa}%0A*Layanan:* ${formData.layanan}%0A*Alamat Asal:* ${formData.asal}%0A*Alamat Tujuan:* ${formData.tujuan}%0A*Detail/Berat Barang:* ${formData.detail}%0A%0AMohon informasi lebih lanjut terkait tarif dan penjemputan. Terima kasih.`;
    const targetNumber = "6288212309927";
    const url = `https://wa.me/${targetNumber}?text=${text}`;
    window.open(url, '_blank');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6">
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-navy/60 backdrop-blur-sm"
          />

          {/* Modal Content */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-3xl bg-white rounded-[2rem] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50 sticky top-0 z-10">
              <div>
                <h3 className="font-heading text-2xl font-bold text-navy">Mulai Pengiriman</h3>
                <p className="text-sm text-gray-500 mt-1">Isi detail di bawah untuk dihubungkan ke WhatsApp kami.</p>
              </div>
              <button 
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-500 hover:text-orange hover:border-orange transition-colors"
              >
                <SafeIcon icon={LuX} className="w-5 h-5" />
              </button>
            </div>

            {/* Form Body (Scrollable) */}
            <div className="p-6 md:p-8 overflow-y-auto">
              <form id="booking-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Nama Lengkap *</label>
                    <input 
                      type="text" name="nama" required value={formData.nama} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all placeholder-gray-400 bg-gray-50 hover:bg-white"
                      placeholder="Masukkan nama Anda"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Nomor WhatsApp *</label>
                    <input 
                      type="number" name="wa" required value={formData.wa} onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all placeholder-gray-400 bg-gray-50 hover:bg-white"
                      placeholder="Contoh: 08123456789"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Pilihan Layanan *</label>
                  <select 
                    name="layanan" value={formData.layanan} onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all bg-gray-50 hover:bg-white cursor-pointer"
                  >
                    <option value="Garuda Reguler (G-Reg)">Garuda Reguler (G-Reg)</option>
                    <option value="Garuda Kilat (G-Kilat)">Garuda Kilat (G-Kilat)</option>
                    <option value="Garuda Kargo Darat & Udara">Garuda Kargo Darat & Udara</option>
                    <option value="Garuda Towing">Garuda Towing</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Alamat Pengambilan *</label>
                    <textarea 
                      name="asal" required value={formData.asal} onChange={handleChange} rows="3"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all resize-none placeholder-gray-400 bg-gray-50 hover:bg-white"
                      placeholder="Detail alamat lengkap..."
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-navy mb-1.5">Alamat Tujuan *</label>
                    <textarea 
                      name="tujuan" required value={formData.tujuan} onChange={handleChange} rows="3"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all resize-none placeholder-gray-400 bg-gray-50 hover:bg-white"
                      placeholder="Detail alamat lengkap..."
                    ></textarea>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Detail & Berat Barang *</label>
                  <textarea 
                    name="detail" required value={formData.detail} onChange={handleChange} rows="2"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-orange focus:ring-2 focus:ring-orange/20 outline-none transition-all resize-none placeholder-gray-400 bg-gray-50 hover:bg-white"
                    placeholder="Contoh: 2 Kardus besar isi pakaian, 15kg"
                  ></textarea>
                </div>
              </form>
            </div>

            {/* Footer Action */}
            <div className="px-6 py-5 border-t border-gray-100 bg-white sticky bottom-0 z-10">
              <button 
                type="submit"
                form="booking-form"
                className="font-heading w-full bg-orange hover:bg-[#d94f1e] text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all shadow-lg shadow-orange/30"
              >
                Kirim via WhatsApp
                <SafeIcon icon={LuArrowRight} className="w-6 h-6" />
              </button>
            </div>

          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}